/*
 * vms-mast-art.js
 * ---------------------------------------------------------------------------
 * Composites a picture of a real pipe mast (fixed pipes + telescoping poles +
 * couplers) from one sprite sheet, onto a canvas, at any scale and in either
 * orientation. Standalone on purpose: window.VMSMastArt has no dependency on
 * the configurator, so extension-pipe PDPs or a future kit-builder can call it.
 *
 * PROVENANCE
 * Geometry ported from the owner's offline tool at
 *   D:\Dropbox\videomountstore-work\SHOPIFY\Pipe-Compositor
 * (read pipe-compositor.html for the readable original). That tool was built to
 * generate one-off 1000x1000 product photos with a human nudging pixels; this is
 * the live shopper-facing port, and it changes four things deliberately. Each
 * change was verified against a Python dry run of the same math before shipping.
 *
 * 1. THE ONE-INCH-COLUMN MODEL, and it is not optional.
 *    Every sprite column is EXACTLY one inch and every x position is derived from
 *    an inch position, never from accumulated tile widths. The original advances
 *    a cursor by each tile's measured source width (94 or 95 px, so 1.0 or 1.0106
 *    in), which is invisible at its native 94 px/in. At the 3 to 22 px/in a live
 *    configurator renders at, rounding every column independently loses up to half
 *    a pixel per column: a 30 in pipe drew 210 px where 30 in needs 222 px, a 5
 *    percent shortfall. Since pieces are POSITIONED from true inches, that
 *    shortfall opened a visible WHITE GAP before the next coupler. This is exactly
 *    the failure the original's own comment warns about (never compute overlap from
 *    nominal inches and tile positions from measured widths in two different ways).
 *    Each tile now spans round(startIn * ppi) to round(endIn * ppi): one inch give
 *    or take a pixel, and the total is pinned to the exact length with zero drift.
 *    The +/-1 px source variance is an artifact of how the original render was
 *    sliced, not real geometry, and discarding it is correct.
 *
 * 2. ARBITRARY TELESCOPING EXTENSION. The original renders only the two endpoints
 *    (collapsed / extended). A mast is set to whatever the drop needs, so the outer
 *    tube's offset is interpolated. That interpolation is EXACTLY linear, which is
 *    what makes drawn length equal requested length at every setting:
 *      t=0 -> 2 + (collapsed-2)            = collapsed
 *      t=1 -> (innerCols-2) + outerCols    = extended
 *
 * 3. DIMENSIONAL INLINE COUPLERS. The original places couplers as separate rows
 *    with a whitespace gap, because it was making product photos, not assemblies.
 *    Everything here shares one scale (sprite band 182 px = 1.936 in, the true OD
 *    of 1-1/2 in NPT pipe; coupler 249 px = 2.649 in long, 210 px = 2.234 in OD),
 *    so the coupler is drawn IN LINE, overlapping each pipe by
 *    (2.649 - 1.5) / 2 = 0.574 in. That reproduces exactly the 1.5 in net add per
 *    joint that the solver's COUPLER_ADD and 11AC001's custom.length_offset both
 *    already assume. Couplers paint LAST so the wider sleeve swallows the pipe
 *    ends the way it does in reality.
 *
 * 4. NO CANVAS AT NATIVE SCALE, and the original's two taste knobs dropped.
 *    The original renders at 400 px tall (207 px/in) and rotates through a
 *    3000x3000 scratch canvas. A 72 in pole that way is ~6 megapixels and a combo
 *    assembly ~24 MP, which exceeds iOS Safari's ~16.7 MP canvas cap and renders
 *    BLANK on iPhones. Here the canvas is the display size (a 440x48 slot is 21k
 *    pixels), so there is nothing to trip. Dropped with it: the 3 px horizontal
 *    "overlap nudge" and 3 px vertical inner offset, both knobs for hiding a seam
 *    at native scale. At display scale 3 native px is under a pixel, and the
 *    vertical offset actively fought the truth: a telescoping pole is concentric
 *    and the sprites already carry the real diameter step (inner sliding section
 *    1.574 in, outer 1.894 in, both measured off the source art).
 *
 * SPRITE
 * assets/vms-pipe-sprite.png, 1384x210, 13 slices, 90.7 KB, ONE request. The
 * original library is 40 PNGs / 598 KB because it cycles 13 interchangeable middle
 * columns to avoid visible tiling in a high-resolution product photo. At display
 * scale that cycling is worth max 10/255 and mean 0.23/255 per pixel (measured), so
 * one repeated middle per part type is used instead. Repeating also guarantees the
 * telescoping adjustment holes land on an even 1 inch pitch, which is what the real
 * pole has. The URL is passed in, never hardcoded: Liquid resolves it via asset_url
 * so it stays version-controlled and cache-busted.
 */
(function () {
  'use strict';

  var SPRITE_W = 1384, SPRITE_H = 210;

  // [sx, sy, sw, sh] into the sprite sheet.
  var R = {
    'inner-thread':  [0,    14, 95, 182],   // large-diameter threaded end, couples to the mast
    'inner-large':   [95,   14, 95, 182],   // large-diameter shoulder
    'inner-step':    [190,  14, 95, 182],   // steps down to the sliding section
    'inner-mid':     [285,  14, 94, 182],   // repeatable sliding section, one adjustment hole
    'inner-cap':     [379,  14, 95, 182],   // sliding-section end cap
    'outer-end':     [474,  14, 94, 182],   // fixed end carrying the locking hole
    'outer-end2':    [568,  14, 94, 182],
    'outer-mid':     [662,  14, 94, 182],   // repeatable outer body
    'outer-cap':     [756,  14, 94, 182],   // threaded outer cap
    'fixed-thread':  [850,  14, 95, 182],   // fixed pipe left thread
    'fixed-mid':     [945,  14, 95, 182],   // repeatable fixed body
    'fixed-thread2': [1040, 14, 95, 182],   // fixed pipe right thread
    'coupler':       [1135,  0, 249, 210]
  };

  var NATIVE_PPI     = 94;                        // one sprite column = one inch
  var COUPLER_NET_ADD = 1.5;                      // inches per joint; matches 11AC001's metafield
  var COUPLER_LEN_IN  = R.coupler[2] / NATIVE_PPI; // 2.649 in
  var COUPLER_OD_IN   = R.coupler[3] / NATIVE_PPI; // 2.234 in
  var PIPE_OD_IN      = 182 / NATIVE_PPI;          // 1.936 in

  // ---- break glyph, the drafting break line --------------------------------
  // [NOTE] A long mast forces ONE px-per-inch down onto everything else in the
  // drawing, so the head, the plate and the TV starve. The standard drafting
  // answer is a BREAK LINE. Owner ruling 2026-09-06 (rev 2.1), from his own
  // sketch: the PIPE RUNS THROUGH the break, two angled lines cross it, and ONLY
  // the thin diagonal strip between the two lines is blank. Not a blank rectangle
  // with lines floating in it, and no zigzag cut ends. The CALLER chooses the
  // bands (see SPEC-mast-break-2026-09-06.md sections 10 and 11 and chooseBreakBands
  // in the scene); this file only draws the glyph, so the scene canvas, the
  // drag-time plain pipe and the build cards all get one implementation.
  var BREAK_MARK_SPAN  = 1.6;   // each line spans this many girths ACROSS (0.8 girth either side of
                                // the axis), so it pokes past the pipe; ~0.46 girth along the length
  var BREAK_MARK_SLANT = 30 * Math.PI / 180;  // each line leans this far off the ACROSS (girth) axis
  var BREAK_BAND_PX    = 10;    // width of the blank diagonal band, measured along the length axis,
                                // between the two lines. A symbol, so it is a fixed px, never scaled
  var BREAK_MARK_W     = 2;     // line width in px, held the SAME at every scale: the owner's sketch
                                // is bold, and it is a symbol, not a measurement
  // [NOTE] The scene's part-label leaders draw at the page foreground colour. Here
  // the sprite art is theme-independent grey metal, so a fixed dark ink at 70
  // percent alpha reads on both the grey pipe and the blank band. A caller on a
  // themed surface may override it through breakBand.ink.
  var BREAK_INK = 'rgba(31, 31, 31, 0.7)';

  // ---- sprite loading, one shared promise for the whole page ----------------
  var spritePromise = null;
  function loadSprite(url) {
    if (spritePromise) return spritePromise;
    spritePromise = new Promise(function (resolve, reject) {
      var img = new Image();
      // Same-origin in practice (asset_url), but harmless and needed if the sprite
      // is ever moved to the Files CDN, since we read the canvas in the self-test.
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('vms-mast-art: sprite failed to load')); };
      img.src = url;
    });
    return spritePromise;
  }

  // ---- part sequences, ONE ENTRY PER INCH ----------------------------------
  function fixedKeys(length) {
    var out = ['fixed-thread'], i;
    for (i = 0; i < Math.max(0, length - 2); i++) out.push('fixed-mid');
    out.push('fixed-thread2');
    return out;
  }

  function innerKeys(collapsed, extended) {
    var innerCols = extended - collapsed + 4;
    var out = ['inner-thread', 'inner-large', 'inner-step'], i;
    for (i = 0; i < innerCols - 4; i++) out.push('inner-mid');
    out.push('inner-cap');
    return out;
  }

  function outerKeys(collapsed) {
    var outerCols = collapsed - 2;
    var out = ['outer-end', 'outer-end2'], i;
    for (i = 0; i < outerCols - 3; i++) out.push('outer-mid');
    out.push('outer-cap');
    return out;
  }

  function clamp(v, lo, hi) { return Math.min(Math.max(v, lo), hi); }
  function fin(v) { return typeof v === 'number' && isFinite(v); }

  // Inches from the telescoping unit's left edge to the outer tube's left edge.
  function teleOuterOffsetIn(collapsed, extended, current) {
    var innerCols = extended - collapsed + 4;
    var cur = clamp(current, collapsed, extended);
    var t = extended === collapsed ? 0 : (cur - collapsed) / (extended - collapsed);
    return 2 + t * ((innerCols - 2) - 2);
  }

  function pieceLength(p) {
    return p.kind === 'tele' ? clamp(p.current, p.collapsed, p.extended) : p.length;
  }

  // The contiguous run of a plain (repeatable) tile in a key list, as [from, to)
  // inches (each tile is one inch): the tile at index a..b spans [a, b + 1].
  function plainRun(keys, midKey) {
    var a = -1, b = -1;
    for (var i = 0; i < keys.length; i++) {
      if (keys[i] === midKey) { if (a < 0) a = i; b = i; }
    }
    return a < 0 ? null : { from: a, to: b + 1 };
  }

  /**
   * The PLAIN, cuttable spans of a telescoping unit, in inches from the unit's own
   * left (ceiling) edge, so a break chooser can treat it like a fixed pipe.
   *
   * Revision 4 (owner 2026-09-06, clarifying the section 13 withdrawal): "I don't
   * want the break in the smaller diameter part of the adjustable pipes, but in the
   * larger diameter 'outer' portion of the pipes I want it." So a telescoping piece
   * contributes EXACTLY ONE candidate: the OUTER tube's plain body. The EXPOSED
   * inner sliding run (the smaller-diameter part) is NEVER a candidate; the
   * revision 3 inner-mid branch that used to return a second span was DELETED here
   * on that ruling and must not come back, because a cut through the exposed inner
   * strip is a cut through the very thing the customer reads as "adjustable".
   *
   * [NOTE] TILE CLASSIFICATION, from the sprite keys outerKeys returns (each key is
   * one 1 inch tile, measured off assets/vms-pipe-sprite.png):
   *   OUTER tube keys: 'outer-end' (fixed end carrying the locking hole),
   *     'outer-end2' (its neighbour), then 'outer-mid' (repeatable body, PLAIN),
   *     then 'outer-cap' (threaded cap). End features excluded = outer-end,
   *     outer-end2 (ceiling end) and outer-cap (head end); the plain span is the
   *     outer-mid run, returned at the outer tube's DRAWN position
   *     (teleOuterOffsetIn). The band inside it shows outer-tube texture on both
   *     sides of the cut; the opaque outer strip hides the inner there anyway, and a
   *     real cut through a concentric pole cuts both tubes.
   * The span is returned WITHOUT the STUB_IN margin: the caller (the chooser)
   * applies stubs uniformly, exactly as it does to a fixed piece. The outer-mid run
   * is collapsed - 5 inches long, so a pole is a candidate only when
   * collapsed - 5 - 2 * STUB_IN >= MIN_BREAK_IN, i.e. collapsed >= 19 (EXT2036,
   * EXT3260 and EXT4472 qualify; EXT0812 and EXT-1220 never do).
   */
  function telePlainSpans(collapsed, extended, current) {
    var ox = teleOuterOffsetIn(collapsed, extended, current);
    var spans = [];
    // Outer tube plain span ONLY: the outer-mid run, offset to the outer's drawn
    // position. The exposed inner run is intentionally omitted (revision 4).
    var oR = plainRun(outerKeys(collapsed), 'outer-mid');
    if (oR) spans.push({ from: ox + oR.from, to: ox + oR.to });
    return spans;
  }

  /**
   * Total assembled length in inches for a spec. Pure, no canvas, no sprite:
   * callers use it to size their slot BEFORE the sprite has loaded, and the
   * self-test uses it to assert against the solver.
   * @param {Array} pieces
   * @returns {number}
   */
  function totalInches(pieces) {
    if (!pieces || !pieces.length) return 0;
    var sum = 0;
    for (var i = 0; i < pieces.length; i++) sum += pieceLength(pieces[i]);
    return sum + COUPLER_NET_ADD * (pieces.length - 1);
  }

  // ---- drawing -------------------------------------------------------------
  function drawStrip(ctx, sprite, keys, startIn, ppi, bandTop, bandH) {
    for (var i = 0; i < keys.length; i++) {
      var r = R[keys[i]];
      var xa = Math.round((startIn + i) * ppi);
      var xb = Math.round((startIn + i + 1) * ppi);
      ctx.drawImage(sprite, r[0], r[1], r[2], r[3], xa, bandTop, Math.max(1, xb - xa), bandH);
    }
  }

  function paint(ctx, sprite, pieces, ppi, bandH, cplH, H) {
    var bandTop = Math.round((H - bandH) / 2);
    var joints = [], cursor = 0, i, p;

    // pass 1: pipes
    for (i = 0; i < pieces.length; i++) {
      p = pieces[i];
      if (p.kind === 'tele') {
        drawStrip(ctx, sprite, innerKeys(p.collapsed, p.extended), cursor, ppi, bandTop, bandH);
        var ox = teleOuterOffsetIn(p.collapsed, p.extended, p.current);
        // The outer strip is OPAQUE, so painting it over the inner is the whole
        // telescoping illusion. No masking, no alpha, nothing to get wrong.
        drawStrip(ctx, sprite, outerKeys(p.collapsed), cursor + ox, ppi, bandTop, bandH);
      } else {
        drawStrip(ctx, sprite, fixedKeys(p.length), cursor, ppi, bandTop, bandH);
      }
      cursor += pieceLength(p);
      if (i < pieces.length - 1) { joints.push(cursor); cursor += COUPLER_NET_ADD; }
    }

    // pass 2: couplers LAST, centred on each joint
    var overlapIn = (COUPLER_LEN_IN - COUPLER_NET_ADD) / 2;
    var r = R.coupler, cTop = Math.round((H - cplH) / 2);
    for (i = 0; i < joints.length; i++) {
      var xa = Math.round((joints[i] - overlapIn) * ppi);
      var xb = Math.round((joints[i] - overlapIn + COUPLER_LEN_IN) * ppi);
      ctx.drawImage(sprite, r[0], r[1], r[2], r[3], xa, cTop, Math.max(1, xb - xa), cplH);
    }
  }

  // ---- break glyph ---------------------------------------------------------
  // An offscreen canvas that draws in CSS coordinates: its backing store is
  // dpr-scaled and its context pre-scaled to match, so callers work in px and the
  // device resolution is handled once, here. drawImage source rectangles read the
  // BACKING pixels, so a slice out of one of these multiplies its CSS span by dpr.
  function offscreen(wCss, hCss, dpr) {
    var c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(wCss * dpr));
    c.height = Math.max(1, Math.round(hCss * dpr));
    var cx = c.getContext('2d');
    if (cx) cx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { c: c, cx: cx };
  }

  // Clip the drawing to ONE side of a break line, in strip space. The line passes
  // through (cx, girth/2) tilted BREAK_MARK_SLANT off the across axis, direction
  // (sin, cos). `keepLeft` keeps the smaller-x (upper) side, else the larger-x
  // (lower) side. The line is extended well past the girth so the clip covers the
  // full pipe height and beyond.
  function clipBreakLine(sx, cx, girth, keepLeft) {
    var dirx = Math.sin(BREAK_MARK_SLANT), diry = Math.cos(BREAK_MARK_SLANT);
    var T = girth * 3 + 40, BIG = 1e5;
    var ax = cx - dirx * T, ay = girth / 2 - diry * T;   // one far end of the line
    var bx = cx + dirx * T, by = girth / 2 + diry * T;   // the other
    sx.beginPath();
    sx.moveTo(ax, ay);
    sx.lineTo(bx, by);
    if (keepLeft) { sx.lineTo(-BIG, by); sx.lineTo(-BIG, ay); }
    else { sx.lineTo(BIG, by); sx.lineTo(BIG, ay); }
    sx.closePath();
    sx.clip();
  }

  // The two break lines for ONE gap, in strip space, centred on xc = gap start +
  // G/2, one at xc - B/2 and one at xc + B/2. Each spans 0.8 girth either side of
  // the axis (poking past the pipe), tilted BREAK_MARK_SLANT off the across axis.
  function drawBreakLines(sx, ink, girth, xc) {
    sx.strokeStyle = ink;
    sx.lineWidth = BREAK_MARK_W;
    sx.lineCap = 'round';
    var dirx = Math.sin(BREAK_MARK_SLANT), diry = Math.cos(BREAK_MARK_SLANT);
    var yHalf = (BREAK_MARK_SPAN / 2) * girth;   // 0.8 girth either side of the axis
    var tHalf = yHalf / diry;                     // param length to reach that y
    var xHalf = tHalf * dirx;
    [xc - BREAK_BAND_PX / 2, xc + BREAK_BAND_PX / 2].forEach(function (lx) {
      sx.beginPath();
      sx.moveTo(lx - xHalf, girth / 2 - yHalf);
      sx.lineTo(lx + xHalf, girth / 2 + yHalf);
      sx.stroke();
    });
  }

  // Composite a mast with N bands cut out of it, in UNROTATED strip space. `bands`
  // is an array of {fromPx, toPx, glyphPx}, sorted and non-overlapping, each inside
  // [0, runPx]. Returns a canvas visLenPx long by girth tall (CSS px), plus the
  // breakPx array so callers can position relative to each gap.
  //
  // [RULE, owner 2026-09-06] THE PIPE RUNS THROUGH THE BREAK. Each slice is drawn
  // EXTENDED by one glyph gap G into the removed run on any cut end, then clipped to
  // the outer side of that gap's break line: the slice above a gap keeps the side
  // LEFT of line 1 (xc - B/2), the slice below keeps the side RIGHT of line 2
  // (xc + B/2). Only the thin diagonal band between the two lines is left blank; the
  // two lines are drawn over the pipe last. The extension is plain pipe by
  // construction (the band was chosen inside plain pipe with 3 in stubs).
  function brokenStrip(sprite, pieces, ppi, bandH, cplH, girth, runPx, bands, ink, dpr) {
    var i, sumRemoved = 0, sumGlyph = 0;
    for (i = 0; i < bands.length; i++) {
      sumRemoved += bands[i].toPx - bands[i].fromPx;
      sumGlyph += bands[i].glyphPx;
    }
    var visLenPx = runPx - sumRemoved + sumGlyph;

    // Paint the WHOLE mast once, unchanged, then take the slices BETWEEN the bands.
    // Painting the full run and slicing is what keeps every joint, every coupler
    // and both pipe ends drawn exactly as they are unbroken; only plain pipe goes.
    var full = offscreen(runPx, girth, dpr);
    if (full.cx) paint(full.cx, sprite, pieces, ppi, bandH, cplH, girth);

    var strip = offscreen(visLenPx, girth, dpr);
    var sx = strip.cx;
    var breakPx = [];
    if (!sx || !full.cx) return { c: strip.c, visLenPx: visLenPx, breakPx: breakPx };

    // Walk the n + 1 slices. Slice i is source [src, srcEnd) placed at dest; it has
    // a gap ABOVE when a band sits above it (i > 0) and a gap BELOW when one sits
    // below (i < n). It extends by that gap's G into the removed run and is clipped
    // to the pipe side of the gap's line, so the extension fills the gap up to the
    // line. Source coordinates are in the full canvas's backing pixels (the dpr
    // factor); dest coordinates are CSS (the strip context is dpr-scaled).
    var src = 0, dest = 0, gaps = [];
    for (i = 0; i <= bands.length; i++) {
      var srcEnd = i < bands.length ? bands[i].fromPx : runPx;
      var segW = srcEnd - src;
      var gAbove = i > 0 ? bands[i - 1].glyphPx : 0;
      var gBelow = i < bands.length ? bands[i].glyphPx : 0;
      var dStart = dest - gAbove, dEnd = dest + segW + gBelow;
      var sStart = src - gAbove, sEnd = srcEnd + gBelow;
      sx.save();
      // gap above: its dest region is [dest - gAbove, dest); its centre and line 2.
      if (gAbove) clipBreakLine(sx, (dest - gAbove / 2) + BREAK_BAND_PX / 2, girth, false);
      // gap below: its dest region is [dest + segW, dest + segW + gBelow); line 1.
      if (gBelow) clipBreakLine(sx, (dest + segW + gBelow / 2) - BREAK_BAND_PX / 2, girth, true);
      sx.drawImage(full.c, sStart * dpr, 0, (sEnd - sStart) * dpr, girth * dpr,
                   dStart, 0, dEnd - dStart, girth);
      sx.restore();
      dest += segW;
      if (i < bands.length) {
        breakPx.push({ start: dest, glyph: bands[i].glyphPx });
        gaps.push(dest + bands[i].glyphPx / 2);   // gap centre, for the lines
        dest += bands[i].glyphPx;
        src = bands[i].toPx;
      }
    }
    // The lines LAST, over the pipe, so they read as ink crossing it.
    for (i = 0; i < gaps.length; i++) drawBreakLines(sx, ink, girth, gaps[i]);

    return { c: strip.c, visLenPx: visLenPx, breakPx: breakPx };
  }

  /**
   * Render a mast onto a canvas.
   *
   * @param {HTMLCanvasElement} canvas
   * @param {Array} pieces  assembly order, ceiling end first. Each entry is
   *        {kind:'fixed', length:30} or
   *        {kind:'tele', collapsed:20, extended:36, current:28}
   *        Couplers are inserted automatically between consecutive pipes.
   * @param {Object} opts
   *        spriteUrl  {string}  required
   *        lengthPx   {number}  px along the mast's length (the long axis)
   *        maxPpi     {number}  px per inch ceiling, so a very short mast does not
   *                             render as a comically fat pipe. Default 22.
   *        vertical   {boolean} true = ceiling at the top, mast running down
   *        breakBands {Array}   optional [{fromIn, toIn, glyphPx, ink}] in MAST
   *                             inches from the ceiling end, one per long pipe.
   *                             Removes plain pipe from each [fromIn, toIn] and
   *                             draws a break glyph in each fixed glyphPx gap. The
   *                             CALLER guarantees each band holds only plain pipe;
   *                             see chooseBreakBands. A band outside the run or
   *                             removing under 1 px is dropped. `breakBand`
   *                             (singular) is accepted as a one-element array.
   * @returns {Promise<{ppi:number, totalIn:number, lengthPx:number, girthPx:number,
   *                    breakPx?:Array<{start:number, glyph:number}>}>}
   */
  function render(canvas, pieces, opts) {
    opts = opts || {};
    var totalIn = totalInches(pieces);
    if (!canvas || !pieces || !pieces.length || totalIn <= 0) {
      return Promise.reject(new Error('vms-mast-art: nothing to render'));
    }

    var lengthPx = Math.max(1, Math.round(opts.lengthPx || 440));
    var maxPpi = opts.maxPpi || 22;
    var ppi = Math.min(lengthPx / totalIn, maxPpi);

    var bandH = Math.max(1, Math.round(PIPE_OD_IN * ppi));
    var cplH  = Math.max(1, Math.round(COUPLER_OD_IN * ppi));
    var girth = Math.max(bandH, cplH);
    var runPx = Math.max(1, Math.round(totalIn * ppi));

    // Validate the break bands against the drawn run. Kept in px so the "shorter
    // than 1 px" test is exact, and any band outside the run or too thin to see is
    // silently dropped: the file's contract is that a bad band draws whole, never
    // throws. glyphPx is the FIXED symbol height in px; it never scales with ppi.
    // breakBand (singular) is accepted as a one-element array.
    var raw = opts.breakBands || (opts.breakBand ? [opts.breakBand] : []);
    var ink = BREAK_INK, bands = [], j;
    for (j = 0; j < raw.length; j++) {
      var bb = raw[j];
      if (!bb || !fin(bb.fromIn) || !fin(bb.toIn) || !fin(bb.glyphPx)) continue;
      var fromPx = Math.round(bb.fromIn * ppi);
      var toPx = Math.round(bb.toIn * ppi);
      var glyphPx = Math.round(bb.glyphPx);
      if (fromPx >= 0 && toPx <= runPx && (toPx - fromPx) >= 1 && glyphPx >= 1) {
        if (typeof bb.ink === 'string') ink = bb.ink;
        bands.push({ fromPx: fromPx, toPx: toPx, glyphPx: glyphPx });
      }
    }
    bands.sort(function (a, b) { return a.fromPx - b.fromPx; });
    // Drop any band overlapping the previous one, so the slices never cross.
    var clean = [];
    for (j = 0; j < bands.length; j++) {
      if (!clean.length || bands[j].fromPx >= clean[clean.length - 1].toPx) clean.push(bands[j]);
    }
    bands = clean;

    // With bands the drawn run is shorter by the removed pipe and longer by one
    // glyph gap each. Everything else (girth, the sprite scale) is untouched.
    var sumRemoved = 0, sumGlyph = 0;
    for (j = 0; j < bands.length; j++) { sumRemoved += bands[j].toPx - bands[j].fromPx; sumGlyph += bands[j].glyphPx; }
    var lenAxisPx = bands.length ? (runPx - sumRemoved + sumGlyph) : runPx;

    // Device-pixel-ratio aware, capped at 2: past 2 the extra pixels buy nothing
    // on this art and only cost memory on high-DPR phones.
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var cssW = opts.vertical ? girth : lenAxisPx;
    var cssH = opts.vertical ? lenAxisPx : girth;

    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    canvas.style.width = cssW + 'px';
    canvas.style.height = cssH + 'px';

    return loadSprite(opts.spriteUrl).then(function (sprite) {
      var ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(dpr, dpr);
      if (opts.vertical) {
        // Rotate the drawing space so the strip's x axis runs DOWN the canvas:
        // (x, y) -> (girth - y, x). The pipe is symmetric about its axis, so the
        // incidental horizontal mirror is invisible.
        ctx.translate(girth, 0);
        ctx.rotate(Math.PI / 2);
      }
      if (bands.length) {
        // Composite in unrotated strip space, then blit through whatever transform
        // the orientation set above, so ONE code path serves both. drawImage with
        // an explicit CSS dest size lets the dpr-scaled context place it 1:1.
        var strip = brokenStrip(sprite, pieces, ppi, bandH, cplH, girth, runPx, bands, ink, dpr);
        ctx.drawImage(strip.c, 0, 0, strip.visLenPx, girth);
        return { ppi: ppi, totalIn: totalIn, lengthPx: strip.visLenPx, girthPx: girth,
                 breakPx: strip.breakPx };
      }
      paint(ctx, sprite, pieces, ppi, bandH, cplH, girth);
      return { ppi: ppi, totalIn: totalIn, lengthPx: runPx, girthPx: girth };
    });
  }

  /**
   * Translate a solver build into a spec. Keeps the mapping in ONE place so the
   * configurator never hand-rolls it.
   *
   * The telescoping unit is placed LAST (nearest the head) so the adjustment is
   * the final joint before the TV, and fixed pipes are ordered longest first from
   * the ceiling. Neither is a structural requirement: the solver does not care
   * about order and any order threads up the same, so this is purely a consistent
   * way to draw it.
   *
   * @param {Array} solverPieces components with nominal_length or length_min/max
   * @param {number} mastTargetIn what the pipe stack alone must reach
   */
  function specFromBuild(solverPieces, mastTargetIn) {
    var fixed = [], tele = null, i, p;
    for (i = 0; i < solverPieces.length; i++) {
      p = solverPieces[i];
      if (p.length_min != null) tele = p;
      else fixed.push(p);
    }
    fixed.sort(function (a, b) { return b.nominal_length - a.nominal_length; });

    var spec = [];
    for (i = 0; i < fixed.length; i++) spec.push({ kind: 'fixed', length: fixed[i].nominal_length });
    if (tele) {
      // Back out the telescoping setting this build needs: whatever the fixed
      // pipes and coupler adds do not already cover, clamped to the unit's range.
      var fixedSum = 0;
      for (i = 0; i < fixed.length; i++) fixedSum += fixed[i].nominal_length;
      var couplers = solverPieces.length - 1;
      var need = mastTargetIn - fixedSum - COUPLER_NET_ADD * couplers;
      spec.push({
        kind: 'tele',
        collapsed: tele.length_min,
        extended: tele.length_max,
        current: clamp(Math.round(need * 2) / 2, tele.length_min, tele.length_max)
      });
    }
    return spec;
  }

  window.VMSMastArt = {
    render: render,
    totalInches: totalInches,
    specFromBuild: specFromBuild,
    // The telescoping unit's ONE plain cuttable span (its outer tube body, rev 4),
    // so break choosers (the scene and the cards) can treat a tele like a fixed pipe.
    // See telePlainSpans.
    telePlainSpans: telePlainSpans,
    COUPLER_NET_ADD: COUPLER_NET_ADD,
    // exposed for the configurator's self-test panel
    _internals: { R: R, fixedKeys: fixedKeys, innerKeys: innerKeys, outerKeys: outerKeys,
                  teleOuterOffsetIn: teleOuterOffsetIn, telePlainSpans: telePlainSpans,
                  PIPE_OD_IN: PIPE_OD_IN,
                  COUPLER_LEN_IN: COUPLER_LEN_IN, SPRITE_W: SPRITE_W, SPRITE_H: SPRITE_H }
  };
})();
